import { Injectable } from '@angular/core';
import { LoggerService } from '../../../shared/services/logger.service';

export interface CommercialLogMetadata {
    eventType: 'page-load' | 'button-action' | 'service';
    pageName: string;
    actionName: string;
    outcome: 'success' | 'failure' | 'loaded' | 'invalid';
    policyNumber: string;
    policyType: string;
    lineOfBusiness: string;
    claimsDisplayCount: string;
    alphaState: string;
    previousUrl: string;
    claimNumber?: string;
    claimStatus?: string;
    incidentDate?: string;
    lossCause?: string;
    serviceName?: string;
    errorCode?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CommercialLoggingService {
    logPageLoad(logger: LoggerService, pageName: string, metadata: Partial<CommercialLogMetadata>): void {
        logger.info('Commercial.PageLoad', this.buildMetadata({
            ...metadata,
            eventType: 'page-load',
            pageName,
            actionName: 'page-init',
            outcome: 'loaded'
        }));
    }

    logButtonAction(
        logger: LoggerService,
        pageName: string,
        actionName: string,
        outcome: CommercialLogMetadata['outcome'],
        metadata: Partial<CommercialLogMetadata>
    ): void {
        logger.info('Commercial.ButtonAction', this.buildMetadata({
            ...metadata,
            eventType: 'button-action',
            pageName,
            actionName,
            outcome
        }));
    }

    logServiceSuccess(
        logger: LoggerService,
        pageName: string,
        serviceName: string,
        actionName: string,
        metadata: Partial<CommercialLogMetadata>
    ): void {
        logger.info('Commercial.Service', this.buildMetadata({
            ...metadata,
            eventType: 'service',
            pageName,
            serviceName,
            actionName,
            outcome: 'success'
        }));
    }

    logServiceFailure(
        logger: LoggerService,
        pageName: string,
        serviceName: string,
        actionName: string,
        metadata: Partial<CommercialLogMetadata>,
        error: any
    ): void {
        logger.error('Commercial.Service', this.buildMetadata({
            ...metadata,
            eventType: 'service',
            pageName,
            serviceName,
            actionName,
            outcome: 'failure',
            errorCode: this.extractErrorCode(error)
        }));
    }

    updatePreviousUrlContext(): void {
        window.sessionStorage.setItem('previousUrl', window.location.href);
    }

    private buildMetadata(metadata: Partial<CommercialLogMetadata>): CommercialLogMetadata {
        return {
            eventType: metadata.eventType || 'service',
            pageName: metadata.pageName || 'UNKNOWN',
            actionName: metadata.actionName || 'UNKNOWN',
            outcome: metadata.outcome || 'success',
            policyNumber: metadata.policyNumber || '',
            policyType: metadata.policyType || 'UNKNOWN',
            lineOfBusiness: metadata.lineOfBusiness || 'UNKNOWN',
            claimsDisplayCount: this.normalizeClaimCount(metadata.claimsDisplayCount),
            alphaState: metadata.alphaState || 'UNKNOWN',
            previousUrl: metadata.previousUrl || window.sessionStorage.getItem('previousUrl') || '',
            claimNumber: metadata.claimNumber || '',
            claimStatus: metadata.claimStatus || '',
            incidentDate: metadata.incidentDate || '',
            lossCause: metadata.lossCause || '',
            serviceName: metadata.serviceName || '',
            errorCode: metadata.errorCode || ''
        };
    }

    private normalizeClaimCount(claimsDisplayCount: any): string {
        if (claimsDisplayCount === undefined || claimsDisplayCount === null || claimsDisplayCount === '') {
            return 'UNKNOWN';
        }
        return String(claimsDisplayCount);
    }

    private extractErrorCode(error: any): string {
        if (!error) {
            return 'UNKNOWN';
        }
        return String(error.status || error.code || error.message || 'UNKNOWN');
    }
}
